import {
  getOrganizationTreeModel,
  type getVisibleProjectPaginationCandidate,
} from '@/features/organization-project-picker/lib/organizationTree';
import { organizationProjectsInfiniteQuery } from '@/features/organization-project-picker/services/organizationProjectQueries';
import type {
  OrganizationSummary,
  Project,
} from '@/features/organization-project-picker/types/organizationProjectPicker';
import type {
  OrganizationProjectBranch,
  OrganizationProjectBranchState,
} from '@/features/organization-project-picker/types/organizationTree';
import { useInfiniteQueryObservers } from '@/shared/hooks/useInfiniteQueryObservers';
import {
  flattenInfiniteItems,
  shouldFetchNextVirtualPageAtIndex,
} from '@/shared/lib/infiniteQuery';
import { useCallback, useMemo } from 'react';

/**
 * Derives the organization→project tree from query-backed organizations and UI
 * expansion state. Query cache remains the only owner of project data.
 *
 * `activeProject` is an optional sidebar affordance: when provided, it is shown
 * at the top of its owning branch before that branch's first page loads.
 */
export function useOrganizationTree(
  organizations: readonly OrganizationSummary[],
  expandedOrganizationIds: ReadonlySet<string>,
  activeProject?: Project,
) {
  const expandedOrganizations = useMemo(
    () =>
      organizations.filter((organization) =>
        expandedOrganizationIds.has(organization.organizationId),
      ),
    [expandedOrganizationIds, organizations],
  );
  const observerEntries = useMemo(
    () =>
      expandedOrganizations.map((organization) => ({
        key: organization.organizationId,
        options: organizationProjectsInfiniteQuery(organization.organizationId),
      })),
    [expandedOrganizations],
  );
  const projectQueriesByOrganizationId = useInfiniteQueryObservers(observerEntries);
  const projectBranches = useMemo(() => {
    const branches = new Map<string, OrganizationProjectBranch>();

    for (const organization of expandedOrganizations) {
      const projectResult = projectQueriesByOrganizationId.get(organization.organizationId);
      const loadedProjects = flattenInfiniteItems(projectResult?.data);
      const projects =
        activeProject &&
        activeProject.organizationId === organization.organizationId &&
        !loadedProjects.some((project) => project.projectId === activeProject.projectId)
          ? [activeProject, ...loadedProjects]
          : loadedProjects;
      let state: OrganizationProjectBranchState = 'ready';
      if (projectResult?.isPending && !projectResult.data) state = 'pending';
      else if (projectResult?.isError && !projectResult.data) state = 'failed';
      else if (!projectResult) state = 'pending';
      else if (projects.length === 0) state = 'empty';

      branches.set(organization.organizationId, {
        state,
        projects,
        totalCount: projectResult?.data?.pages[0]?.totalCount,
        hasNextPage: !!projectResult?.hasNextPage,
        showFooter:
          state === 'ready' &&
          !!(projectResult?.isFetchingNextPage || projectResult?.isFetchNextPageError),
      });
    }

    return branches;
  }, [activeProject, expandedOrganizations, projectQueriesByOrganizationId]);
  const tree = useMemo(
    () => getOrganizationTreeModel(organizations, expandedOrganizationIds, projectBranches),
    [expandedOrganizationIds, organizations, projectBranches],
  );
  const getProjectQuery = useCallback(
    (organizationId: string) => projectQueriesByOrganizationId.get(organizationId),
    [projectQueriesByOrganizationId],
  );
  const retryProjectBranch = useCallback(
    (organizationId: string) => {
      void projectQueriesByOrganizationId.get(organizationId)?.refetch();
    },
    [projectQueriesByOrganizationId],
  );
  const fetchNextProjectPage = useCallback(
    (candidate: ReturnType<typeof getVisibleProjectPaginationCandidate>, isScrolling: boolean) => {
      if (!candidate) return;

      const projectQuery = projectQueriesByOrganizationId.get(candidate.organizationId);
      if (
        projectQuery &&
        shouldFetchNextVirtualPageAtIndex({
          isScrolling,
          itemCount: candidate.projectCount,
          lastVisibleIndex: candidate.projectIndex,
          hasNextPage: projectQuery.hasNextPage,
          isFetchingNextPage: projectQuery.isFetchingNextPage,
          isFetchNextPageError: projectQuery.isFetchNextPageError,
        })
      ) {
        void projectQuery.fetchNextPage();
      }
    },
    [projectQueriesByOrganizationId],
  );

  return { tree, getProjectQuery, retryProjectBranch, fetchNextProjectPage };
}
